import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { DatePipe } from "@angular/common";
import { ContentDispatcherComponent } from "../content/dispatcher/content-dispatcher/content-dispatcher.component";
import { MarkdownRendererComponent } from "./markdown-renderer/markdown-renderer.component";
import { ToolCallHintComponent } from "../content/inline/tool-call-hint/tool-call-hint.component";
import { DebugModeService } from "../../services/debug-mode.service";
import type { ResolvedToolCall } from "../../model/block.types";

/**
 * Renders a single assistant message:
 * - Markdown content via MarkdownRendererComponent (marked + hljs + katex, fully owned)
 * - Rich tool/endpoint/geocoding blocks via ContentDispatcherComponent
 * - Animated typing cursor when streaming
 *
 * Messages with is_reasoning=true are filtered at the list level
 * and rendered as ReasoningStepComponent indicators instead.
 */
@Component({
  selector: "lib-chat-assistant-message",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, MarkdownRendererComponent, ContentDispatcherComponent, ToolCallHintComponent],
  templateUrl: "./chat-assistant-message.component.html",
  styleUrl: "./chat-assistant-message.component.scss",
})
export class ChatAssistantMessageComponent {
  readonly message = input.required<any>();

  readonly toolCalls = input<ResolvedToolCall[]>([]);

  /** True only for the last message while the agent is still streaming. */
  readonly showCursor = input<boolean>(false);

  protected readonly debugMode = inject(DebugModeService);

  protected readonly textContent = computed<string>(() => {
    const content = this.message()?.content;
    if (typeof content === "string") return content;
    if (Array.isArray(content)) {
      return content
        .filter((p: any) => p?.type === "text")
        .map((p: any) => p.text ?? "")
        .join("");
    }
    return "";
  });

  /** Message creation time — uses the message's createdAt if available, else render time. */
  protected readonly timestamp = computed<Date>(() => {
    const ts = this.message()?.createdAt;
    return ts ? new Date(ts) : new Date();
  });
}
